import { Application } from 'express';
import { Logger } from '@unidev-hub/logger';
import { ConfigManager } from '@unidev-hub/config';
import { DocumentationOptions } from '../../types';
import { setupSwagger } from './swagger';
import { setupRedoc } from './redoc';

/**
 * Setup API documentation
 *
 * @param app Express application
 * @param config Configuration manager
 * @param logger Logger instance
 * @param options Documentation options
 */
export async function setupDocumentation(
  app: Application,
  config: ConfigManager,
  logger: Logger,
  options: DocumentationOptions = {}
): Promise<void> {
  try {
    // Check if documentation is enabled
    const isEnabled = options.enabled !== false &&
      config.get<boolean>('api.docs.enabled', process.env.NODE_ENV !== 'production');

    if (!isEnabled) {
      logger.info('API documentation is disabled');
      return;
    }

    // Determine documentation type
    const docType = options.type || config.get<string>('api.docs.type', 'swagger');

    logger.debug(`Setting up API documentation with type: ${docType}`);

    // Setup the appropriate documentation type
    switch (docType) {
      case 'swagger':
        await setupSwagger(app, config, logger, options);
        break;

      case 'redoc':
        await setupRedoc(app, config, logger, options);
        break;

      default:
        logger.warn(`Unsupported documentation type: ${docType}, using Swagger instead`);
        await setupSwagger(app, config, logger, options);
    }

    logger.info(`API documentation setup successfully with type: ${docType}`);
  } catch (error) {
    logger.error('Failed to setup API documentation', error as Error);
    // Don't throw, as this is not critical for the application
    logger.warn('Continuing without API documentation');
  }
}

// Re-export documentation implementations
export { setupSwagger } from './swagger';
export { setupRedoc } from './redoc';
